/**
 * Shared wizard state for the EDD Generation Flow (the 5-step /new wizard).
 *
 * One state object is threaded through every step so a user can move back and forth without losing
 * their work. A "work order" is one uploaded source (or the built-in synthetic sample); a "format" is
 * one selected catalog template or saved config. The cartesian product (work orders × formats) is what
 * gets previewed in Step 3 and generated in Step 4.
 */
import type { FlowSource } from "../../api/generateFlow";

/** One uploaded work order (or the synthetic sample) the run reads its Out* data from. */
export interface WorkOrder {
  /** Stable client id (the SourceUpload id, or "synthetic"). */
  id: string;
  /** What the run sends on the wire. */
  source: FlowSource;
  /** Display name (work order number / filename / "Sample data"). */
  label: string;
  kind: "synthetic" | "csv_zip" | "output_mdb";
  /** From the upload response — surfaced as "Found N samples, M analytes…". */
  samples?: number | null;
  analytes?: number | null;
  matrices?: number | null;
  workOrderNumber?: string | null;
  warnings?: string[];
}

/** One selected EDD format (a catalog template or a saved config). */
export interface SelectedFormat {
  /** Stable id (template id or config id). */
  id: string;
  selector: { template?: string; config_id?: string };
  name: string;
  family?: string;
  outputFormat?: string;
  /** Backend's human-readable deliverable label, e.g. "4 files · TXT". */
  outputLabel?: string;
  fieldCount?: number;
  needsCrossTable?: boolean;
  kind: "template" | "config";
}

export type OutputFormat = "DELIMITED" | "TSV" | "EXCEL" | "XML" | "ACCESS2" | "ACCESS7";

export interface WizardState {
  workOrders: WorkOrder[];
  formats: SelectedFormat[];
  outputFormats: OutputFormat[];
  runName: string;
}

export const SYNTHETIC_WORK_ORDER: WorkOrder = {
  id: "synthetic",
  source: "synthetic",
  label: "Sample data (built-in)",
  kind: "synthetic",
};
